class HeaderComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.resizeObserver.observe(this);
    this.stickyMode = this.getAttribute("data-sticky-type");
    this.offscreen = false;
    this.isStuck = false;

    this.menuDrawerHiddenWidth = null;
    this.intersectionObserver = null;
    this.lastScrollTop = 0;

    this.timeout = null;
    this.animationDelay = 150;

    // smooth-fix: in "always" mode, data-sticky-state only matters for the transparent
    // header and the reveal-logo option. Flipping it otherwise forced the browser to
    // restyle the whole (very large) header for ~100ms every time it stuck/unstuck.
    this.needsStickyState =
      this.stickyMode !== "always" ||
      this.hasAttribute("transparent") ||
      this.classList.contains("header--reveal-logo-on-sticky") ||
      !!this.querySelector(".header-logo__image-container--inverse");

    if (this.stickyMode) {
      this.observeStickyPosition(this.stickyMode === "always");

      if (this.stickyMode === "scroll-up" || this.stickyMode === "always") {
        this._onScroll = theme.utils.rafThrottle(this.handleWindowScroll.bind(this));
        document.addEventListener("scroll", this._onScroll, { passive: true });
      }
    }
  }

  disconnectedCallback() {
    this.resizeObserver.disconnect();
    this.intersectionObserver?.disconnect();
    if (this._onScroll) {
      this._onScroll.cancel?.();
      document.removeEventListener("scroll", this._onScroll);
    }
    document.body.style.setProperty("--header-height", "0px");
  }

  // Only touch the DOM when a value really changes (every write invalidates styles).
  setData(key, value) {
    if (this.dataset[key] !== value) this.dataset[key] = value;
  }

  resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;
    // Use the observer's own measurement: no extra layout read.
    const height = Math.round(entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height);
    if (height === this._lastHeight) return;
    this._lastHeight = height;
    cancelAnimationFrame(this._resizeRaf);
    this._resizeRaf = requestAnimationFrame(() => {
      document.body.style.setProperty("--header-height", `${height}px`);
    });
  });

  observeStickyPosition(alwaysSticky = true) {
    if (this.intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.isStuck = !isIntersecting;
        if (this.needsStickyState) this.setData("stickyState", isIntersecting ? "inactive" : "active");
      } else {
        this.offscreen = !isIntersecting || this.dataset.stickyState === "active";
      }
    }, config);

    this.intersectionObserver.observe(this);
  }

  handleWindowScroll() {
    if (!this.offscreen && this.stickyMode !== "always") return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.lastScrollTop;

    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }

    if (this.stickyMode === "always") {
      // smooth-fix: use the IntersectionObserver result instead of
      // getBoundingClientRect(), which forced a full-page layout on every scroll frame.
      if (!this.isStuck) {
        this.setData("scrollDirection", "none");
      } else {
        this.setData("scrollDirection", isScrollingUp ? "up" : "down");
      }

      this.lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute("data-animating");

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.offscreen = false;
        this.setData("stickyState", "inactive");
        this.setData("scrollDirection", "none");
      } else {
        // show sticky header when scrolling up
        this.setData("stickyState", "active");
        this.setData("scrollDirection", "up");
      }
    } else if (this.dataset.stickyState === "active") {
      this.setData("scrollDirection", "none");
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute("data-animating", "");

      this.timeout = setTimeout(() => {
        this.setData("stickyState", "idle");
        this.removeAttribute("data-animating");
      }, this.animationDelay);
    } else {
      this.setData("scrollDirection", "none");
      this.setData("stickyState", "idle");
    }

    this.lastScrollTop = scrollTop;
  }
}

if (!customElements.get("header-component"))
  customElements.define("header-component", HeaderComponent);

function calculateHeaderGroupHeight(
  header = document.querySelector('#header-component'),
  headerGroup = document.querySelector('#header-group')
) {
  if (!headerGroup) return 0;

  let totalHeight = 0;
  const children = headerGroup.children;

  for (let i = 0; i < children.length; i++) {
    const element = children[i];
    if (element === header || !(element instanceof HTMLElement)) continue;
    totalHeight += element.offsetHeight;
  }

  // If the header is transparent and has a sibling section, add the height of the header to the total height
  if (header instanceof HTMLElement && header.hasAttribute('transparent') && !header.parentElement?.nextElementSibling) {
    return totalHeight + header.offsetHeight;
  }

  return totalHeight;
}

function updateHeaderHeights() {
  const header = document.querySelector('header-component');

  // Early exit if no header - nothing to do
  if (!(header instanceof HTMLElement)) return;

  const headerGroupHeight = calculateHeaderGroupHeight(header);
  const value = `${headerGroupHeight}px`;
  // Writing a variable on <body> restyles the whole page, so skip it when unchanged.
  if (document.body.style.getPropertyValue('--header-group-height') !== value) {
    document.body.style.setProperty('--header-group-height', value);
  }
}

function setheaderRowHeight() {
  const headerMenu = document.querySelector('.header__row [data-main-menu]');
  if (!headerMenu) return;

  const { height } = headerMenu.closest('.header__row').getBoundingClientRect();
  const value = `${height}px`;
  if (document.body.style.getPropertyValue('--header-row-menu-height') !== value) {
    document.body.style.setProperty('--header-row-menu-height', value);
  }
}

if (document.readyState === "complete") {
  setheaderRowHeight();
  updateHeaderHeights();
} else {
  window.addEventListener("load", () => {
    updateHeaderHeights();
    setheaderRowHeight();
  });
}
